import { Button } from '@/components/ui/button'
import { cn } from '@/utilities/ui'
import Link from 'next/link'
import React from 'react'
import { Media } from '@/components/media'

import type { Page, Post } from '@payload-types'
import type { Media as MediaType } from '@payload-types'
import type { VariantProps } from 'class-variance-authority'
import { buttonVariants } from '@/components/ui/button'

type ButtonProps = VariantProps<typeof buttonVariants>

export type CMSLinkType = {
  appearance?: 'inline' | ButtonProps['variant'] | 'icon-only'
  children?: React.ReactNode
  className?: string
  label?: string | null
  newTab?: boolean | null
  reference?: {
    relationTo: 'pages' | 'posts' | 'categories' | 'tags'
    value: Page | Post | string | number
  } | null
  size?: ButtonProps['size'] | null
  type?: 'custom' | 'reference' | null
  url?: string | null
  iconBefore?: MediaType | null
  iconAfter?: MediaType | null
  iconClassName?: string
  iconOnly?: boolean
}

export const CMSLink: React.FC<CMSLinkType> = (props) => {
  const {
    type,
    appearance = 'inline',
    children,
    className,
    label,
    newTab,
    reference,
    size: sizeFromProps,
    url,
    iconBefore,
    iconAfter,
    iconClassName,
    iconOnly = false,
  } = props

  const getHref = () => {
    if (
      type !== 'reference' ||
      !reference ||
      typeof reference.value !== 'object' ||
      !reference.value.slug
    ) {
      return url
    }

    const { relationTo, value } = reference
    const slug = value.slug

    switch (relationTo) {
      case 'pages':
        return `/${slug}`
      case 'posts': {
        // Safely access displayId from Post type
        const postValue = value as Post
        return postValue.displayId ? `/${slug}/${postValue.displayId}` : `/${slug}`
      }
      case 'tags':
        return `/haberleri/${slug}`
      case 'categories':
        return `/haberler/${slug}`
      default:
        return `/${relationTo}/${slug}`
    }
  }

  const href = getHref()

  if (!href) return null

  const isIconOnly = iconOnly || appearance === 'icon-only'
  const size = appearance === 'link' ? undefined : sizeFromProps
  const newTabProps = newTab ? { rel: 'noopener noreferrer', target: '_blank' } : {}

  const renderContent = () => {
    // For icon-only links, only show the first available icon
    if (isIconOnly) {
      if (iconBefore) {
        return (
          <Media
            resource={iconBefore}
            className={cn('size-5', iconClassName)}
            htmlElement="span"
            alt={label || 'Icon'}
          />
        )
      }
      if (iconAfter) {
        return (
          <Media
            resource={iconAfter}
            className={cn('size-5', iconClassName)}
            htmlElement="span"
            alt={label || 'Icon'}
          />
        )
      }
      // If iconOnly is true but no icons provided, fallback to text
    }

    return (
      <>
        {iconBefore && (
          <Media
            resource={iconBefore}
            className={cn('size-4', iconClassName)}
            htmlElement="span"
            alt={label || 'Icon'}
          />
        )}
        {!isIconOnly && label && <span>{label}</span>}
        {!isIconOnly && children && children}
        {iconAfter && (
          <Media
            resource={iconAfter}
            className={cn('size-4', iconClassName)}
            htmlElement="span"
            alt={label || 'Icon'}
          />
        )}
      </>
    )
  }

  /* Ensure we don't break any styles set by richText */
  if (appearance === 'inline') {
    return (
      <Link
        className={cn(isIconOnly && 'flex items-center justify-center', className)}
        href={href || url || ''}
        {...newTabProps}
        aria-label={isIconOnly && label ? label : undefined}
      >
        {renderContent()}
      </Link>
    )
  }

  return (
    <Button
      asChild
      className={className}
      size={isIconOnly && !size ? 'icon' : size}
      variant={appearance === 'icon-only' ? 'ghost' : appearance}
    >
      <Link
        href={href || url || ''}
        {...newTabProps}
        aria-label={isIconOnly && label ? label : undefined}
      >
        {renderContent()}
      </Link>
    </Button>
  )
}
